import { MerchantApi } from "@/api";
import { ROLE_CODES } from "@/constants";
import type { UserInfo } from "@/hooks/use-user-info";
import { isMerchantAdmin } from "@/utils/role.utils";

export type MerchantFilterOption = {
  id?: number;
  merchantCode: string;
  merchantName: string;
};

export async function fetchMerchantsForUserFilter(
  userInfo: Pick<UserInfo, "roleCode" | "merchantId"> | null
): Promise<MerchantFilterOption[]> {
  if (!userInfo) return [];

  if (userInfo.roleCode === ROLE_CODES.LOYYAL_ADMIN) {
    const merchantRes = await MerchantApi.getAllMerchants();
    return Array.isArray(merchantRes.data) ? merchantRes.data : [];
  }

  if (userInfo.roleCode === ROLE_CODES.PROGRAM_ADMIN) {
    const merchantRes = await MerchantApi.getMerchantDataUsingProgramId();
    return Array.isArray(merchantRes.data) ? merchantRes.data : [];
  }

  if (userInfo.roleCode === ROLE_CODES.MERCHANT_ADMIN && userInfo.merchantId) {
    const merchantRes = await MerchantApi.getMerchantsFromDB({
      merchantId: userInfo.merchantId,
      pageNumber: 0,
      pageSize: 100,
      sortOrder: "desc",
      sortBy: "createdDate",
      country: null,
      merchantType: null,
      merchantName: null,
    });
    const merchantList = merchantRes?.data?.merchantVOList || [];
    return merchantList.map((merchant: any) => ({
      id: merchant?.id,
      merchantCode: merchant?.merchantCode || "",
      merchantName: merchant?.merchantName || "",
    }));
  }

  return [];
}

/** Merchant admin is always scoped to their own merchant */
export function getScopedMerchantId(
  userInfo: Pick<UserInfo, "roleCode" | "merchantId"> | null
): number | undefined {
  if (userInfo?.roleCode === ROLE_CODES.MERCHANT_ADMIN && userInfo.merchantId) {
    return userInfo.merchantId;
  }
  return undefined;
}

/** Default merchant filter code for merchant admin (auto-applied) */
export function getDefaultMerchantFilterCode(
  userInfo: Pick<UserInfo, "roleCode" | "merchantCode"> | null
): string | undefined {
  if (isMerchantAdmin(userInfo?.roleCode) && userInfo?.merchantCode) {
    return userInfo.merchantCode;
  }
  return undefined;
}

/** merchantId + merchantCode for API payloads (merchant admin scoped) */
export function getMerchantFilterParams(
  userInfo: Pick<UserInfo, "roleCode" | "merchantId" | "merchantCode"> | null,
  filters: { merchantCode?: string } = {}
): { merchantId?: number; merchantCode?: string } {
  const merchantId = getScopedMerchantId(userInfo);
  const merchantCode =
    filters.merchantCode || getDefaultMerchantFilterCode(userInfo);
  return {
    ...(merchantId ? { merchantId } : {}),
    ...(merchantCode ? { merchantCode } : {}),
  };
}
